// Период, параметры запросов и переходы в список для вкладки «Аналитика».
// Даты - строки yyyy-mm-dd по местному времени (UTC+5), арифметика - через UTC, чтобы часовой пояс браузера ничего не сдвигал
import { DocumentFilters } from '../types';
import { AnalyticsFilters, BucketBy, LeadKind, Period, Preset, SliceKey } from './analyticsTypes';

/** Как на сервере: период аналитики - не больше 366 дней */
export const MAX_DAYS = 366;
/** С какой длины периода динамика по умолчанию строится по неделям */
export const WEEKLY_FROM_DAYS = 92;

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const toUtc = (d: string) => new Date(`${d}T00:00:00Z`);

/** «Сегодня» по местному времени документов (UTC+5) */
export const localToday = (): string => new Date(Date.now() + 5 * 3600e3).toISOString().slice(0, 10);

export function addDays(d: string, n: number): string {
  const t = toUtc(d);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

/** Сколько дней от a до b (b раньше - отрицательное) */
export const diffDays = (a: string, b: string): number => Math.round((toUtc(b).getTime() - toUtc(a).getTime()) / 86400000);

const validDay = (d: string): boolean => DAY_RE.test(d) && !isNaN(toUtc(d).getTime()) && toUtc(d).toISOString().slice(0, 10) === d;

export const PRESETS: Array<{ value: Preset; label: string; days: number }> = [
  { value: '7', label: '7 дней', days: 7 },
  { value: '30', label: '30 дней', days: 30 },
  { value: '90', label: '90 дней', days: 90 },
  { value: '365', label: 'Год', days: 365 },
  { value: 'custom', label: 'Период', days: 0 }
];

export const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilters = {
  preset: '30', from: '', to: '', type: '', route: '', marking: '', pharmacy: [], region: []
};

export interface ResolvedPeriod extends Period {
  days: number;
  /** Период задан неверно: запросов нет, на экране - пояснение */
  error: string | null;
}

/** Период по фильтрам: пресет - последние N дней по сегодня включительно, «Период» - свои даты с проверкой */
export function resolvePeriod(f: AnalyticsFilters): ResolvedPeriod {
  if (f.preset !== 'custom') {
    const preset = PRESETS.find(p => p.value === f.preset) || PRESETS[1];
    const to = localToday();
    return { from: addDays(to, -(preset.days - 1)), to, days: preset.days, error: null };
  }
  const fail = (error: string): ResolvedPeriod => ({ from: f.from, to: f.to, days: 0, error });
  if (!f.from || !f.to) return fail('Укажите начало и окончание периода');
  if (!validDay(f.from) || !validDay(f.to)) return fail('Неверная дата периода');
  if (f.from > f.to) return fail('Начало периода позже его окончания');
  const days = diffDays(f.from, f.to) + 1;
  if (days > MAX_DAYS) return fail(`Период аналитики - не больше ${MAX_DAYS} дней`);
  return { from: f.from, to: f.to, days, error: null };
}

/** Прошлый период той же длины вплотную перед текущим - как на сервере */
export function prevPeriodOf(period: Period): Period {
  const days = diffDays(period.from, period.to) + 1;
  const to = addDays(period.from, -1);
  return { from: addDays(to, -(days - 1)), to };
}

/** Параметры запросов аналитики: пустые значения не передаются */
export function periodParams(f: AnalyticsFilters, period: Period): Record<string, string> {
  const params: Record<string, string> = { from: period.from, to: period.to };
  if (f.type) params.type = f.type;
  if (f.route) params.route = f.route;
  if (f.marking) params.marking = f.marking;
  if (f.pharmacy.length) params.pharmacy = f.pharmacy.join(',');
  if (f.region.length) params.region = f.region.join(',');
  return params;
}

/** Заданы ли фильтры, кроме периода по умолчанию */
export const hasAnalyticsFilters = (f: AnalyticsFilters): boolean => Boolean(
  f.preset !== DEFAULT_ANALYTICS_FILTERS.preset || f.type || f.route || f.marking || f.pharmacy.length || f.region.length
);

/** Длительность в часах: '40 мин' / '5,3 ч' / '3,2 дн.' */
export function fmtHours(h?: number | null): string {
  if (h === null || h === undefined || !isFinite(h)) return '-';
  if (h <= 0) return '0 мин';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} мин`;
  if (h < 48) return `${h.toLocaleString('ru-RU', { maximumFractionDigits: h < 10 ? 1 : 0 })} ч`;
  const days = h / 24;
  return `${days.toLocaleString('ru-RU', { maximumFractionDigits: days < 10 ? 1 : 0 })} дн.`;
}

export const KIND_LABELS: Record<LeadKind, string> = {
  all: 'Все документы',
  direct: 'Прямое перемещение',
  via: 'Через РЦ',
  return: 'Возврат'
};

export const KIND_COLORS: Record<LeadKind, string> = {
  all: '#334155',
  direct: '#2563eb',
  via: '#7c3aed',
  return: '#0d9488'
};

/** Цвета событий динамики */
export const EVENT_COLORS = { done: '#16a34a', errors: '#dc2626', extended: '#2563eb', autoCancel: '#dc2626', autoHide: '#f59e0b' };

/** Фильтр списка для вида документа */
export function kindPatch(kind: LeadKind): Partial<DocumentFilters> {
  if (kind === 'direct') return { type: '5', route: 'direct' };
  if (kind === 'via') return { type: '5', route: 'via' };
  if (kind === 'return') return { type: '4', route: '' };
  return {};
}

/**
 * Фильтры списка, соответствующие когорте аналитики: те же фильтры + «Создан с / по» = период.
 * Скрытые документы в аналитике учитываются, поэтому и в списке - «Все».
 */
export function listPatch(f: AnalyticsFilters, period: Period, extra: Partial<DocumentFilters> = {}): Partial<DocumentFilters> {
  return {
    type: f.type, route: f.route, marking: f.marking, pharmacy: f.pharmacy, region: f.region,
    createdFrom: period.from, createdTo: period.to, hidden: 'all', ...extra
  };
}

/** Короткая дата для оси: '05.10' */
export const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const dmy = (d: string) => `${dm(d)}.${d.slice(0, 4)}`;

/** Подпись корзины: день - '05.10', неделя - '05.10–11.10' */
export function bucketLabel(date: string, by: BucketBy): string {
  return by === 'week' ? `${dm(date)}–${dm(addDays(date, 6))}` : dm(date);
}

/** День недели даты: 0 - понедельник */
export const dowOf = (d: string): number => (toUtc(d).getUTCDay() + 6) % 7;

/** Заголовок подсказки корзины: 'Пн, 05.10.2026' / 'Неделя 05.10 – 11.10.2026 (в период входит частично)' */
export function bucketTitle(date: string, by: BucketBy, partial: boolean): string {
  if (by === 'day') return `${DOW[dowOf(date)]}, ${dmy(date)}`;
  return `Неделя ${dm(date)} – ${dmy(addDays(date, 6))}${partial ? ' (в период входит частично)' : ''}`;
}

/** Границы корзины внутри периода (неделя на краю периода обрезается по нему) */
export function bucketRange(date: string, by: BucketBy, period: Period): Period {
  if (by === 'day') return { from: date, to: date };
  const end = addDays(date, 6);
  return { from: date < period.from ? period.from : date, to: end > period.to ? period.to : end };
}

/** Упаковки: в старых накладных бывают дробные количества - до одного знака */
export const fmtPack = (v?: number | null): string => (v === null || v === undefined ? '-' : v.toLocaleString('ru-RU', { maximumFractionDigits: 1 }));

/** Срезы товаров: первым - производитель */
export const SLICE_LABELS: Record<SliceKey, string> = {
  producer: 'Производитель',
  type: 'Тип товара',
  rxotc: 'Рецептурность',
  folder: 'Группа 1С',
  mnn: 'МНН'
};

/** Параметр запроса среза */
export const SLICE_PARAMS: Record<SliceKey, string> = { producer: 'producer', type: 'ptype', rxotc: 'rxotc', folder: 'folder', mnn: 'mnn' };

/** Значение среза без признака */
export const SLICE_NONE = '__none';
export const sliceText = (v: string): string => (v === SLICE_NONE ? 'не указано' : v);

/** Дни недели: 0 - понедельник */
export const DOW =['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export const DOW_FULL = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
